import { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Alert,
    CircularProgress, Stack, alpha, IconButton,
} from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Google as GoogleIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

import { auth } from '../firebase';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { trackSignIn } from '../services/analytics';

const SCREENSHOTS = [
    { src: '/assets/screenshots/overview.png', label: 'Analytics Overview', color: '#6366f1' },
    { src: '/assets/screenshots/youtube.png', label: 'YouTube Performance', color: '#FF0000' },
    { src: '/assets/screenshots/instagram.png', label: 'Instagram Insights', color: '#E1306C' },
    { src: '/assets/screenshots/facebook.png', label: 'Facebook Analytics', color: '#1877F2' },
];

function DashboardShowcase() {
    const [active, setActive] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setActive(i => (i + 1) % SCREENSHOTS.length), 3500);
        return () => clearInterval(t);
    }, []);
    const current = SCREENSHOTS[active];
    return (
        <Box sx={{ width: '100%', maxWidth: 520, mt: 3 }}>
            {/* Browser chrome */}
            <Box sx={{
                borderRadius: '14px 14px 0 0',
                bgcolor: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderBottom: 'none',
                px: 2, py: 1.1,
                display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
                <Box sx={{ display: 'flex', gap: 0.7 }}>
                    {['#ff5f57', '#ffbd2e', '#28c840'].map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c, opacity: 0.85 }} />
                    ))}
                </Box>
                <Box sx={{
                    flex: 1, height: 22, borderRadius: '6px',
                    bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', px: 1.5, gap: 1,
                }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: current.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                        socialixer.com/dashboard
                    </Typography>
                </Box>
            </Box>
            {/* Screenshot */}
            <Box sx={{
                position: 'relative', borderRadius: '0 0 14px 14px',
                border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden',
                aspectRatio: '16/9', bgcolor: '#0a0f1e',
                boxShadow: '0 24px 64px -16px rgba(0,0,0,0.7)',
            }}>
                {SCREENSHOTS.map((ss, i) => (
                    <Box key={ss.src} component="img" src={ss.src} alt={ss.label} sx={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'top',
                        transition: 'opacity 0.6s ease', opacity: i === active ? 1 : 0,
                    }} />
                ))}
                <Box sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%',
                    background: 'linear-gradient(to top, rgba(6,9,20,0.85) 0%, transparent 100%)',
                    pointerEvents: 'none',
                }} />
                <Box sx={{ position: 'absolute', bottom: 12, left: 14, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: current.color, boxShadow: `0 0 8px ${current.color}` }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>{current.label}</Typography>
                </Box>
            </Box>
            {/* Dots */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.8, mt: 1.5 }}>
                {SCREENSHOTS.map((_, i) => (
                    <Box key={i} onClick={() => setActive(i)} sx={{
                        height: 3, borderRadius: 99, cursor: 'pointer',
                        width: i === active ? 22 : 7,
                        bgcolor: i === active ? current.color : 'rgba(255,255,255,0.2)',
                        transition: 'all 0.3s ease',
                    }} />
                ))}
            </Box>
        </Box>
    );
}

// Floating social media notification cards
const NOTIF_CARDS = [
    { emoji: '❤️', text: '2.4K likes on your reel', sub: 'Instagram · just now', color: '#f43f5e', left: '8%', dur: '14s', delay: '0s' },
    { emoji: '🔥', text: 'Trending in your niche', sub: 'TikTok · 2 min ago', color: '#f97316', left: '18%', dur: '18s', delay: '3s' },
    { emoji: '👥', text: '+847 new followers today', sub: 'YouTube · 5 min ago', color: '#6366f1', left: '60%', dur: '16s', delay: '1.5s' },
    { emoji: '💬', text: 'AI replied to 34 comments', sub: 'Instagram · 8 min ago', color: '#10d98a', left: '72%', dur: '20s', delay: '5s' },
    { emoji: '📈', text: 'Post reach: 128K impressions', sub: 'LinkedIn · 12 min ago', color: '#3b82f6', left: '30%', dur: '22s', delay: '7s' },
    { emoji: '⚡', text: '94% engagement rate this week', sub: 'Analytics · 15 min ago', color: '#a855f7', left: '82%', dur: '15s', delay: '2s' },
    { emoji: '🎯', text: 'Lead captured from DM', sub: 'Twitter · 20 min ago', color: '#f59e0b', left: '45%', dur: '19s', delay: '9s' },
    { emoji: '🚀', text: 'Scheduled 6 posts this week', sub: 'Socialixer · 1 hr ago', color: '#06b6d4', left: '5%', dur: '17s', delay: '11s' },
];

// Animated floating orb
function Orb({ size, top, left, color, delay = '0s' }) {
    return (
        <Box sx={{
            position: 'absolute', top, left,
            width: size, height: size, borderRadius: '50%',
            background: `radial-gradient(circle at 40% 40%, ${color}, transparent 70%)`,
            filter: 'blur(60px)', opacity: 0.5, pointerEvents: 'none',
            animation: `floatOrb 8s ease-in-out ${delay} infinite alternate`,
            '@keyframes floatOrb': {
                '0%': { transform: 'translateY(0px) scale(1)' },
                '100%': { transform: 'translateY(-30px) scale(1.08)' },
            },
        }} />
    );
}

// Floating notification card that drifts upward
function FloatingCard({ emoji, text, sub, color, left, dur, delay }) {
    return (
        <Box sx={{
            position: 'absolute', bottom: '-120px', left,
            display: 'flex', alignItems: 'center', gap: 1.2,
            px: 2, py: 1.2, borderRadius: '14px',
            bgcolor: alpha('#0d0b2e', 0.75),
            border: `1px solid ${alpha(color, 0.3)}`,
            backdropFilter: 'blur(16px)',
            boxShadow: `0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(color, 0.1)} inset`,
            whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 2,
            animation: `riseUp ${dur} linear ${delay} infinite`,
            '@keyframes riseUp': {
                '0%': { transform: 'translateY(0) scale(0.92)', opacity: 0 },
                '8%': { transform: 'translateY(-60px) scale(1)', opacity: 1 },
                '80%': { transform: 'translateY(-680px) scale(1)', opacity: 0.85 },
                '100%': { transform: 'translateY(-800px) scale(0.95)', opacity: 0 },
            },
        }}>
            <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>{emoji}</Typography>
            <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>{text}</Typography>
                <Typography sx={{ fontSize: '0.63rem', color: alpha(color, 0.85), mt: 0.2, fontWeight: 600 }}>{sub}</Typography>
            </Box>
            {/* Pulse dot */}
            <Box sx={{
                width: 6, height: 6, borderRadius: '50%', bgcolor: color, ml: 0.5, flexShrink: 0,
                boxShadow: `0 0 6px ${color}`,
                animation: 'pulse 1.8s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
            }} />
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Login page component (Firebase Google Auth)
// ---------------------------------------------------------------------------
export function LoginPage() {
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);
    const redirectTo = queryParams.get('redirectTo') || '/app';

    const handleGoogleSignIn = async () => {
        setIsLoading(true);
        setError('');
        try {
            const provider = new GoogleAuthProvider();
            // Force account chooser — lets admin switch accounts on /secret-login
            provider.setCustomParameters({ prompt: 'select_account' });
            await signInWithPopup(auth, provider);
            trackSignIn('google');
            navigate(redirectTo);
        } catch (err) {
            setError(err.message || 'Failed to sign in with Google');
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh', display: 'flex',
            background: 'linear-gradient(135deg, #060611 0%, #0d0b2e 50%, #0a0f1e 100%)',
            overflow: 'hidden', position: 'relative',
        }}>
            {/* Background orbs */}
            <Orb size={500} top="-10%" left="-5%" color="#6366f1" delay="0s" />
            <Orb size={400} top="50%" left="30%" color="#a855f7" delay="2s" />
            <Orb size={350} top="20%" left="70%" color="#10d98a" delay="4s" />
            <Orb size={300} top="70%" left="80%" color="#3b82f6" delay="1.5s" />

            {/* Subtle grid overlay */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)',
                backgroundSize: '60px 60px',
            }} />

            {/* Back button + Logo — top bar */}
            <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 10, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                    onClick={() => navigate('/')}
                    sx={{
                        color: alpha('#fff', 0.6),
                        bgcolor: alpha('#fff', 0.06),
                        border: `1px solid ${alpha('#fff', 0.1)}`,
                        backdropFilter: 'blur(12px)',
                        borderRadius: '12px',
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: alpha('#fff', 0.12),
                            color: '#fff',
                            transform: 'translateX(-2px)',
                        },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 8px 20px rgba(99,102,241,0.45)',
                    }}>
                        <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#fff', letterSpacing: '-0.02em' }}>
                        Socialixer
                    </Typography>
                </Box>
            </Box>

            {/* ── LEFT PANEL — login card ── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flex: 1, px: { xs: 3, md: 6 }, py: 6,
                position: 'relative', zIndex: 3,
            }}>
                {/* Card */}
                <Box sx={{
                    width: '100%', maxWidth: 400,
                    borderRadius: '28px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(32px)',
                    boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)',
                    p: { xs: 4, sm: 5 },
                }}>
                    {/* Mobile: logo + back link */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4, display: { md: 'none' } }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '10px',
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 18 }} />
                            </Box>
                            <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '1rem' }}>Socialixer</Typography>
                        </Stack>
                    </Stack>

                    {/* Heading */}
                    <Typography sx={{
                        fontWeight: 900, fontSize: '1.75rem', color: '#fff',
                        letterSpacing: '-0.03em', lineHeight: 1.2, mb: 1,
                    }}>
                        Welcome back
                    </Typography>
                    <Typography sx={{ color: alpha('#fff', 0.45), fontSize: '0.9rem', mb: 4, lineHeight: 1.5 }}>
                        Sign in to access your dashboard and continue growing.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: '14px', bgcolor: alpha('#ef4444', 0.1), border: `1px solid ${alpha('#ef4444', 0.25)}`, color: '#fca5a5', '& .MuiAlert-icon': { color: '#f87171' } }}>
                            {error}
                        </Alert>
                    )}

                    {/* Google button */}
                    <Button
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        fullWidth
                        size="large"
                        startIcon={isLoading
                            ? <CircularProgress size={18} sx={{ color: '#111' }} />
                            : <GoogleIcon sx={{ fontSize: '20px !important' }} />}
                        sx={{
                            py: 1.6, borderRadius: '14px',
                            background: 'linear-gradient(180deg, #ffffff 0%, #f0f0f0 100%)',
                            color: '#111', fontWeight: 800, fontSize: '0.95rem',
                            textTransform: 'none', letterSpacing: '-0.01em',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.4) inset',
                            transition: 'all 0.2s',
                            '&:hover': {
                                background: '#fff',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                            },
                            '&:active': { transform: 'translateY(0)' },
                            '&.Mui-disabled': { background: alpha('#fff', 0.2), color: alpha('#fff', 0.4) },
                        }}
                    >
                        {isLoading ? 'Signing in…' : 'Continue with Google'}
                    </Button>

                    {/* Divider */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 3 }}>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: alpha('#fff', 0.08) }} />
                        <Typography sx={{ fontSize: '0.72rem', color: alpha('#fff', 0.3), fontWeight: 600 }}>SECURE & PRIVATE</Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: alpha('#fff', 0.08) }} />
                    </Stack>

                    {/* Trust badges */}
                    <Stack direction="row" justifyContent="center" spacing={3}>
                        {['🔒 Encrypted', '⚡ Instant Access', '🛡️ No spam'].map(t => (
                            <Typography key={t} sx={{ fontSize: '0.7rem', color: alpha('#fff', 0.3), fontWeight: 600 }}>{t}</Typography>
                        ))}
                    </Stack>
                </Box>
            </Box>

            {/* ── RIGHT PANEL — brand & dashboard showcase ── */}
            <Box sx={{
                display: { xs: 'none', md: 'flex' },
                flex: 1, flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                px: { md: 4, lg: 6 }, py: 8,
                position: 'relative', zIndex: 3,
                overflow: 'hidden',
            }}>
                {/* Floating social notification cards — scoped to right panel */}
                {NOTIF_CARDS.map(card => <FloatingCard key={card.text} {...card} />)}

                {/* Headline */}
                <Box sx={{ maxWidth: 560, width: '100%' }}>
                    <Typography sx={{
                        fontWeight: 900, fontSize: { md: '2.4rem', lg: '3rem' },
                        lineHeight: 1.1, letterSpacing: '-0.04em', color: '#fff', mb: 2,
                    }}>
                        Your Social Media{' '}
                        <Box component="span" sx={{
                            background: 'linear-gradient(90deg, #6366f1, #a855f7, #10d98a)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        }}>
                            Empire
                        </Box>
                        , Automated.
                    </Typography>
                    <Typography sx={{ color: alpha('#fff', 0.55), fontSize: '1.05rem', mb: 4, maxWidth: 440, lineHeight: 1.65 }}>
                        One dashboard to create, schedule, analyze and engage across every platform — powered by AI.
                    </Typography>
                </Box>

                {/* Dashboard screenshot carousel */}
                <DashboardShowcase />
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// RequireAuth wrapper — gates any component behind the Firebase auth check
//
// E2E bypass rules:
//   ACTIVE  — Playwright injects `window.__E2E_MOCK_AUTH__ = true` via addInitScript().
//             This is ephemeral (per page-load) so it never bleeds into normal sessions.
//   INACTIVE — env var alone, URL params, or stale localStorage do NOT trigger bypass.
//             Normal localhost dev always uses real Firebase auth.
// ---------------------------------------------------------------------------
export function RequireAuth({ children }) {
    // E2E Bypass: Strictly for local development and CI testing.
    // WARNING: This allows bypassing authentication. Ensure REACT_APP_ALLOW_E2E_BYPASS 
    // is NEVER set to 'true' in production environments.
    const isMock = String(process.env.REACT_APP_ENABLE_MOCK_AUTH).toLowerCase() === 'true';

    const [user, setUser] = useState(isMock ? {
        uid: 'e2e-mock-user',
        email: 'e2e@test.com',
        displayName: 'E2E Tester',
    } : null);
    const [loading, setLoading] = useState(!isMock);

    useEffect(() => {
        if (isMock) return;

        // Normal flow: subscribe to Firebase auth state
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMock]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        // Redirect to /login with the intended path so we can come back after sign-in
        return <Navigate to={`/login?redirectTo=${encodeURIComponent(window.location.pathname)}`} replace />;
    }

    return children;
}

export default RequireAuth;
